/**
 * First-login product tour — versioned, client-side persistence.
 *
 * The tour shows automatically the first time a signed-in user reaches the app
 * and again on refresh until they finish or skip it. Completion is persisted
 * per-device in `localStorage` (no backend), keyed by the version below.
 *
 * HOW TO "REFRESH THE TUTORIAL" FOR EVERYONE
 * ------------------------------------------
 * Bump {@link TUTORIAL_VERSION} and deploy. Because each device only stores the
 * last version it completed, every user whose stored value is lower than the
 * new constant will see the walkthrough again on their next visit — i.e.
 * "if we deploy, they'll see it for the first time" again.
 *
 * TRADE-OFF: persistence is per-device, not per-user/cross-device. A user who
 * signs in on a second browser/device will see the tour again there. Moving to
 * cross-device persistence would require a backend (the `update-me`/`get-me`
 * profile API), which lives under `infrastructure/` and is intentionally out of
 * scope for this pass.
 */

/**
 * Bump this (and deploy) to re-trigger the walkthrough for every user.
 * 31: setup checklist (#488). 32: "Record whole sessions" step, shown only to
 * Premium Session Recording switch holders. 33: that step names the consent
 * choices (tap, out loud, without words, or in the app of someone marked
 * "Can give consent"). 34: Easy Read rewrite — at most five steps per audience
 * (members, Independents, providers, Premium Session Recording holders), no
 * beta notice, "Report a problem" named in the Menu step. 35: the Menu step
 * names "Replay set up" (first-run setup runs before the walkthrough). 36: the
 * Calls step tells people a voice says "Remember, all calls are recorded" as
 * each call connects (the restored connecting reminder). 37: production
 * overhaul — five task-oriented steps per audience in nav order (Sessions,
 * Messages, Calls, Alerts, Menu), Forum/Announce folded into Messages, the
 * Calls step notes recording and the in-car video pause, and a clearer,
 * larger, higher-contrast card.
 */
export const TUTORIAL_VERSION = 37

const STORAGE_KEY = 'ascend-tutorial-version'

/** Last tutorial version this device completed/dismissed (0 if never). */
export function getLastSeenTutorialVersion(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return 0
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : 0
  } catch {
    // Private mode / disabled storage: treat as "already seen" so we never
    // trap the user in a tour that can't be persisted as dismissed.
    return TUTORIAL_VERSION
  }
}

/** Persist that this device has finished the current tutorial version. */
export function markTutorialComplete(): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(TUTORIAL_VERSION))
  } catch {
    /* ignore quota / private mode */
  }
}

/** True when the current tutorial version is newer than what this device saw. */
export function shouldShowTutorial(): boolean {
  return getLastSeenTutorialVersion() < TUTORIAL_VERSION
}
